import type { QueryParams } from '@buildex/module-contract';
import { ApiError } from './errors';
import type {
  Approval,
  Chessboard,
  ChessboardUnit,
  HealthStatus,
  ManualPriceInput,
  ManualPriceResult,
  ObjectConfig,
  ObjectConfigInput,
} from './app-api';

/**
 * Мок App API (app-api-service) для VITE_API_MOCK=1: те же пути и формы ответов,
 * что в FRONTEND_GUIDE §6. Состояние живёт в памяти вкладки (ручная цена и решения
 * по approvals меняют шахматку — как в реальной системе после доставки в CRM).
 */

export const MOCK_TENANT = 'demo';
export const MOCK_PROJECT = '5000000001';

/**
 * Стадия запуска объекта в моках (для «дорожки» на Главной):
 *   empty      — шахматки ещё нет (шаг 1 не пройден)
 *   unpriced   — помещения есть, цен нет (шаг 2 не пройден)
 *   priced     — цены есть, конфига ДЦО нет (шаг 3 не пройден)
 *   configured — всё настроено
 * Объекты вне карты считаются configured. Id совпадают с MOCK_PROJECTS модуля ДЦО.
 */
type LaunchStage = 'empty' | 'unpriced' | 'priced' | 'configured';
const MOCK_STAGES: Record<string, LaunchStage> = {
  '5000000004': 'priced',
  '5000000005': 'empty',
  '5000000006': 'priced',
  '5000000008': 'empty',
  '5000000010': 'unpriced',
  '5000000012': 'priced',
  '5000000014': 'unpriced',
};

function stageOf(project: string): LaunchStage {
  return MOCK_STAGES[project] ?? 'configured';
}

const STATUSES = [
  'available',
  'available',
  'sold',
  'booked',
  'available',
  'held',
  'available',
  'sold',
];

function seedUnits(project: string): ChessboardUnit[] {
  const stage = stageOf(project);
  if (stage === 'empty') return [];
  const withPrices = stage !== 'unpriced';
  const units: ChessboardUnit[] = [];
  // Разные guid для разных объектов
  let guid = 1_000_000_000 + (Number(project.slice(-4)) || 0) * 10_000;
  for (const entrance of ['1', '2']) {
    for (let floor = 1; floor <= 9; floor += 1) {
      for (let pos = 1; pos <= 4; pos += 1) {
        guid += 1;
        const rooms = [1, 2, 2, 3][pos - 1] ?? 1;
        const area = 38 + rooms * 14 + (floor % 3);
        const status =
          STATUSES[(floor * 4 + pos + Number(entrance)) % STATUSES.length] ?? 'available';
        // ₸/м² растёт с этажом; в minor units (×100)
        const perSqm = 550_000 + rooms * 20_000 + floor * 3_000;
        units.push({
          unit_guid: String(guid),
          unit_type: 'apartment',
          unit_number: `${floor}${String(pos).padStart(2, '0')}`,
          rooms: String(rooms),
          area_sqm: String(area),
          floor,
          entrance,
          status,
          price:
            status === 'sold' || !withPrices
              ? null
              : { amount_minor: perSqm * area * 100, currency: 'KZT' },
        });
      }
    }
  }
  for (let i = 1; i <= 6; i += 1) {
    guid += 1;
    const sold = i % 2 === 0;
    units.push({
      unit_guid: String(guid),
      unit_type: 'parking',
      unit_number: `P-${i}`,
      rooms: '0',
      area_sqm: '13.0',
      floor: -1,
      entrance: null,
      status: sold ? 'sold' : 'available',
      price: sold || !withPrices ? null : { amount_minor: 350_000_000, currency: 'KZT' },
    });
  }
  return units;
}

interface MockState {
  units: Map<string, ChessboardUnit[]>;
  approvals: Map<number, Approval & { project: string }>;
  configs: Map<string, ObjectConfig>;
  nextApprovalId: number;
}

function seedState(): MockState {
  const units = seedUnits(MOCK_PROJECT);
  const pick = (i: number) => units[i]?.unit_guid ?? '0';
  const approvals: Array<Approval & { project: string }> = [
    {
      id: 41,
      project: MOCK_PROJECT,
      project_guid: MOCK_PROJECT,
      unit_guid: pick(3),
      new_price: { amount_minor: 3_300_000_000, currency: 'KZT' },
      reason: 'sale #3 (every 3) → +500 000',
      status: 'awaiting_approval',
    },
    {
      id: 42,
      project: MOCK_PROJECT,
      project_guid: MOCK_PROJECT,
      unit_guid: pick(8),
      new_price: { amount_minor: 4_150_000_000, currency: 'KZT' },
      reason: 'sale #6 (every 3) → +500 000',
      status: 'awaiting_approval',
    },
    {
      id: 43,
      project: MOCK_PROJECT,
      project_guid: MOCK_PROJECT,
      unit_guid: pick(20),
      new_price: { amount_minor: 5_020_000_000, currency: 'KZT' },
      reason: 'sale #9 (every 3) → +500 000',
      status: 'awaiting_approval',
    },
  ];
  return {
    units: new Map([[MOCK_PROJECT, units]]),
    approvals: new Map(approvals.map((a) => [a.id, a])),
    configs: new Map([
      [
        MOCK_PROJECT,
        {
          advisory_mode: true,
          guardrail_min_price_minor: null,
          guardrail_max_price_minor: null,
          guardrail_max_step_up_minor: 10_000_000,
          forbid_decrease: true,
          planned_avg_price_per_sqm_minor: 90_000_000,
          sales_start_date: '2026-02-01',
          currency: 'KZT',
          tenant_id: MOCK_TENANT,
          project_guid: MOCK_PROJECT,
        },
      ],
    ]),
    nextApprovalId: 44,
  };
}

let state: MockState | null = null;

function getState(): MockState {
  if (state === null) state = seedState();
  return state;
}

/** Помещения объекта; незнакомый объект сидируется лениво по его стадии */
function unitsOf(project: string): ChessboardUnit[] {
  const s = getState();
  let units = s.units.get(project);
  if (units === undefined) {
    units = seedUnits(project);
    s.units.set(project, units);
  }
  return units;
}

/** Конфиг объекта; для стадии configured создаётся лениво */
function configOf(project: string): ObjectConfig | undefined {
  const s = getState();
  const found = s.configs.get(project);
  if (found !== undefined || stageOf(project) !== 'configured') return found;
  const created: ObjectConfig = {
    advisory_mode: true,
    guardrail_min_price_minor: null,
    guardrail_max_price_minor: null,
    guardrail_max_step_up_minor: 10_000_000,
    forbid_decrease: true,
    planned_avg_price_per_sqm_minor: 90_000_000,
    sales_start_date: '2026-02-01',
    currency: 'KZT',
    tenant_id: MOCK_TENANT,
    project_guid: project,
  };
  s.configs.set(project, created);
  return created;
}

/** Approval без служебного поля project (форма ответа §6.2) */
function toApproval(a: Approval & { project: string }): Approval {
  return {
    id: a.id,
    project_guid: a.project_guid,
    unit_guid: a.unit_guid,
    new_price: { ...a.new_price },
    reason: a.reason,
    status: a.status,
  };
}

function applyPrice(project: string, unitGuid: string, amountMinor: number): boolean {
  const unit = unitsOf(project).find((u) => u.unit_guid === unitGuid);
  if (unit === undefined) return false;
  unit.price = { amount_minor: amountMinor, currency: 'KZT' };
  return true;
}

const RE_CHESSBOARD = /^\/objects\/([^/]+)\/chessboard$/;
const RE_CONFIG = /^\/objects\/([^/]+)\/config$/;
const RE_PRICE = /^\/objects\/([^/]+)\/units\/([^/]+)\/price$/;
const RE_DECIDE = /^\/approvals\/(\d+)\/(approve|reject)$/;

/** Сентинел «путь не относится к App API» */
export const NOT_APP_API: unique symbol = Symbol('not-app-api');

export function mockAppApiRequest(
  method: string,
  path: string,
  body: unknown,
  query?: QueryParams,
): unknown | typeof NOT_APP_API {
  const s = getState();

  if (method === 'GET' && path === '/healthz') {
    return { status: 'ok', backend: 'mock', auth: 'mock' } satisfies HealthStatus;
  }

  const chess = RE_CHESSBOARD.exec(path);
  if (method === 'GET' && chess) {
    const project = decodeURIComponent(chess[1] ?? '');
    const units = unitsOf(project);
    return {
      project_guid: project,
      count: units.length,
      units: structuredClone(units),
    } satisfies Chessboard;
  }

  if (method === 'GET' && path === '/approvals') {
    const project = query?.project === undefined ? undefined : String(query.project);
    const approvals = Array.from(s.approvals.values())
      .filter(
        (a) => a.status === 'awaiting_approval' && (project === undefined || a.project === project),
      )
      .map(toApproval);
    return { count: approvals.length, approvals };
  }

  const decide = RE_DECIDE.exec(path);
  if (method === 'POST' && decide) {
    const id = Number(decide[1]);
    const approval = s.approvals.get(id);
    if (approval === undefined || approval.status !== 'awaiting_approval') {
      throw new ApiError(404, `POST ${path} → 404`, {
        detail: 'approval not found, not awaiting approval, or not yours',
      });
    }
    approval.status = decide[2] === 'approve' ? 'pending' : 'rejected';
    if (approval.status === 'pending') {
      applyPrice(approval.project, approval.unit_guid, approval.new_price.amount_minor);
    }
    return toApproval(approval);
  }

  const config = RE_CONFIG.exec(path);
  if (config) {
    const project = decodeURIComponent(config[1] ?? '');
    if (method === 'GET') {
      const found = configOf(project);
      if (found === undefined) {
        throw new ApiError(404, `GET ${path} → 404`, { detail: 'no config for this object' });
      }
      return structuredClone(found);
    }
    if (method === 'PUT') {
      const input = (body ?? {}) as ObjectConfigInput;
      if (input.sales_start_date && !/^\d{4}-\d{2}-\d{2}$/.test(input.sales_start_date)) {
        throw new ApiError(422, `PUT ${path} → 422`, {
          detail: [{ loc: ['body', 'sales_start_date'], msg: 'Input should be a valid date' }],
        });
      }
      const saved: ObjectConfig = {
        advisory_mode: input.advisory_mode ?? false,
        guardrail_min_price_minor: input.guardrail_min_price_minor ?? null,
        guardrail_max_price_minor: input.guardrail_max_price_minor ?? null,
        guardrail_max_step_up_minor: input.guardrail_max_step_up_minor ?? null,
        forbid_decrease: input.forbid_decrease ?? true,
        planned_avg_price_per_sqm_minor: input.planned_avg_price_per_sqm_minor ?? null,
        sales_start_date: input.sales_start_date ?? null,
        currency: input.currency ?? 'KZT',
        tenant_id: MOCK_TENANT,
        project_guid: project,
      };
      s.configs.set(project, saved);
      return structuredClone(saved);
    }
  }

  const price = RE_PRICE.exec(path);
  if (method === 'POST' && price) {
    const project = decodeURIComponent(price[1] ?? '');
    const unit = decodeURIComponent(price[2] ?? '');
    const input = (body ?? {}) as Partial<ManualPriceInput>;
    if (typeof input.price_minor !== 'number' || !Number.isInteger(input.price_minor)) {
      throw new ApiError(422, `POST ${path} → 422`, {
        detail: [{ loc: ['body', 'price_minor'], msg: 'Input should be a valid integer' }],
      });
    }
    if (!applyPrice(project, unit, input.price_minor)) {
      throw new ApiError(404, `POST ${path} → 404`, { detail: 'unit not found' });
    }
    return {
      applied: true,
      unit_guid: unit,
      new_price: { amount_minor: input.price_minor, currency: input.currency ?? 'KZT' },
    } satisfies ManualPriceResult;
  }

  return NOT_APP_API;
}
